const _void = () => {
  // Empty function used as default no-op logger
};

const LOG_LEVELS = {
  LOG: 1,
  DEBUG: 2,
  INFO: 3,
  WARN: 4,
  ERROR: 5,
  TABLE: 6,
} as const;

const LOG_LEVEL_COLORS = {
  LOG: '#181818',
  DEBUG: '#8f8f8f',
  INFO: '#188bea',
  WARN: '#FF9800',
  ERROR: '#e73326',
  TABLE: '#2d9b31',
} as const;

type LOG_LEVEL = keyof typeof LOG_LEVELS;

type HANDLER = (message?: any, ...optionalParams: any[]) => void;

interface TrackingWindow extends Window {
  LOG_LEVEL: any;
}

declare let window: TrackingWindow;

/**
 * A configurable logging utility that provides structured console output with level-based filtering.
 *
 * Features:
 * - Multiple log levels (LOG, DEBUG, INFO, WARN, ERROR, TABLE)
 * - Color-coded output for different log levels
 * - Per-instance enable/disable functionality
 * - Global log level override via window.LOG_LEVEL
 * - Automatic prefixing with logger name
 * - Method chaining support for enable/disable operations
 *
 * @class Logger
 * @example
 * ```typescript
 * const logger = new Logger('MyComponent', 'INFO');
 * logger.enable();
 * logger.info('Component initialized'); // Blue text
 * logger.warn('Deprecated API used');   // Orange text
 * logger.error('Failed to load data');  // Red text
 * ```
 */
export default class Logger {
  name: string;

  level: LOG_LEVEL;

  disabled = true;

  log: HANDLER = _void;

  debug: HANDLER = _void;

  info: HANDLER = _void;

  warn: HANDLER = _void;

  error: HANDLER = _void;

  table: HANDLER = _void;

  /**
   * @constructor
   * @param {string} name - Name of the logger
   * @param level
   */
  constructor(name: string, level: LOG_LEVEL = 'WARN') {
    this.name = name;
    this.level = level;
    this.setMethods();
  }

  static LOG_LEVEL = null;

  getName() {
    return this.name;
  }

  /**
   * Allow us to disable individual loggers
   * @method
   * @memeberof Logger
   */
  disable() {
    this.disabled = true;
    this.setMethods();
    return this;
  }

  /**
   * Allow us to enable individual loggers
   * @method
   * @memeberof Logger
   */
  enable() {
    this.disabled = false;
    this.setMethods();
    return this;
  }

  setMethods() {
    let loggerLevelName = this.level;
    if (Logger.LOG_LEVEL) {
      loggerLevelName = Logger.LOG_LEVEL;
    }

    if (typeof window !== 'undefined' && window.LOG_LEVEL) {
      loggerLevelName = window.LOG_LEVEL;
    }

    const loggerLevel = LOG_LEVELS[loggerLevelName];

    // Only bind methods we explicitly support
    const methods = ['log', 'debug', 'info', 'warn', 'error', 'table'] as const;
    for (const method of methods) {
      const level = method.toUpperCase();
      const typeLevel = LOG_LEVELS[level as keyof typeof LOG_LEVELS];

      if (this.disabled || typeLevel < loggerLevel) {
        // disable binding - Logger has no output
        this[method] = _void;
      } else if (method === 'table') {
        // bind but skip the descriptor
        // eslint-disable-next-line no-console
        this[method] = console[method].bind(console);
      } else {
        const levelColor = LOG_LEVEL_COLORS[level as keyof typeof LOG_LEVEL_COLORS];
        // eslint-disable-next-line no-console
        this[method] = console[method].bind(
          console,
          `%c${this.getName()}\n`,
          `color: ${levelColor}; font-weight: bold`
        );
      }
    }
  }
}
